import type {MutableRefObject} from 'react';
import {useEffect, useRef, useCallback} from 'react';
import debounce from 'lodash/debounce';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updatePagePreview, removePagePreview} from '@Components/poster-editor/poster-editor-reducer';
import type {Page} from '@/poster-whiteboard/page/page.class';
import type {Poster} from '@/poster-whiteboard/poster/poster.class';

const DEBOUNCE_MS = 5000;
const BATCH_SIZE = 5;

// for safari < 16.4
const scheduleIdleCallback = (callback: () => void): number => {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(callback);
  }
  return window.setTimeout(callback);
};

// for safari < 16.4
const cancelCallback = (id: number): void => {
  if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
};

export interface PagePreviewScopeConfig {
  enabled: boolean;
  requiredDimension: number;
}

export interface PagePreviewConfig {
  allPagesBatchedPreviews: PagePreviewScopeConfig;
  currentPageLivePreview: PagePreviewScopeConfig;
  allPagesLivePreview: PagePreviewScopeConfig;
}

interface PageGenerationState {
  lastModified: Date;
  dimension: number;
}

interface PendingGenerationRequest {
  dimension: number;
  abortSignal: AbortSignal | null;
}

interface InFlightGenerationState {
  dimension: number;
  pending: PendingGenerationRequest | null;
}

interface PageListener {
  dimension: number;
  detach: () => void;
  cancelDebounce: () => void;
}

interface CurrentPageListener extends PageListener {
  pageId: string;
}

async function generatePreviewDataUrl(page: Page, maxDimension: number): Promise<string | null> {
  try {
    const src = await page.toDataURL({maxDimension});
    return src || null;
  } catch (error) {
    console.error('Error generating preview:', error);
    return null;
  }
}

const isPageStale = (lastGeneratedStates: Map<string, PageGenerationState>, pageId: string, page: Page, dimension: number): boolean => {
  const lastGenerated = lastGeneratedStates.get(pageId);
  const isContentStale = !lastGenerated || lastGenerated.lastModified !== page.lastModified;
  const isResolutionInsufficient = lastGenerated !== undefined && lastGenerated.dimension < dimension;
  return isContentStale || isResolutionInsufficient;
};

const collectStalePageIds = (pageIds: string[], poster: Poster, dimension: number, lastGeneratedStates: Map<string, PageGenerationState>): string[] => {
  const stalePageIds: string[] = [];
  for (const pageId of pageIds) {
    const page = poster.pages.pagesHashMap[pageId];
    if (page && isPageStale(lastGeneratedStates, pageId, page, dimension)) {
      stalePageIds.push(pageId);
    }
  }
  return stalePageIds;
};

const removePreviewsForDeletedPages = (pageIds: string[], lastGeneratedStates: Map<string, PageGenerationState>, dispatch: ReturnType<typeof useAppDispatch>): void => {
  for (const pageId of lastGeneratedStates.keys()) {
    if (!pageIds.includes(pageId)) {
      lastGeneratedStates.delete(pageId);
      dispatch(removePagePreview(pageId));
    }
  }
};

const generatePreviewsInBatches = (
  pagesToGenerate: string[],
  abortController: AbortController,
  generatePreviewForPage: (pageId: string, dimension: number, abortSignal?: AbortSignal) => Promise<void>,
  dimension: number
): void => {
  const generateBatch = (startIndex: number): void => {
    if (startIndex >= pagesToGenerate.length || abortController.signal.aborted) {
      return;
    }

    const batch = pagesToGenerate.slice(startIndex, startIndex + BATCH_SIZE);
    void Promise.all(batch.map((pageId) => generatePreviewForPage(pageId, dimension, abortController.signal))).then(() => {
      generateBatch(startIndex + BATCH_SIZE);
    });
  };

  generateBatch(0);
};

const queueHigherDimensionPending = (inFlight: InFlightGenerationState, dimension: number, abortSignal: AbortSignal | null): void => {
  if (dimension > inFlight.dimension) {
    const existingPendingDimension = inFlight.pending?.dimension ?? 0;
    if (dimension >= existingPendingDimension) {
      inFlight.pending = {dimension, abortSignal};
    }
  }
};

const isPreviewResultCommittable = (
  dataUrl: string | null,
  inFlightState: InFlightGenerationState,
  poster: Poster,
  pageId: string,
  abortSignal: AbortSignal | undefined
): dataUrl is string => {
  return !!dataUrl && inFlightState.pending === null && !!poster.pages.pagesHashMap[pageId] && !abortSignal?.aborted;
};

const isExistingListenerSufficient = (listener: CurrentPageListener | null, pageId: string, requiredDimension: number): boolean => {
  return listener?.pageId === pageId && listener.dimension >= requiredDimension;
};

const detachListener = (listener: PageListener): void => {
  listener.detach();
  listener.cancelDebounce();
};

const detachCurrentPageListener = (listenerRef: MutableRefObject<CurrentPageListener | null>): void => {
  if (listenerRef.current) {
    detachListener(listenerRef.current);
    listenerRef.current = null;
  }
};

const detachAllPagesListeners = (listenersRef: MutableRefObject<Map<string, PageListener>>): void => {
  for (const listener of listenersRef.current.values()) {
    detachListener(listener);
  }
  listenersRef.current.clear();
};

const createDebouncedModifiedListener = (
  page: Page,
  pageId: string,
  dimension: number,
  generatePreviewForPage: (pageId: string, dimension: number) => Promise<void>
): Omit<PageListener, 'dimension'> => {
  const debouncedGeneration = debounce(
    () => {
      void generatePreviewForPage(pageId, dimension);
    },
    DEBOUNCE_MS,
    {leading: true, maxWait: DEBOUNCE_MS}
  );

  page.on('modified', debouncedGeneration);

  return {
    detach: (): void => {
      page.off('modified', debouncedGeneration);
    },
    cancelDebounce: (): void => {
      debouncedGeneration.cancel();
    },
  };
};

const pruneListenersForRemovedPages = (pageIds: string[], listenersRef: MutableRefObject<Map<string, PageListener>>): void => {
  for (const [pageId, listener] of listenersRef.current.entries()) {
    if (!pageIds.includes(pageId)) {
      detachListener(listener);
      listenersRef.current.delete(pageId);
    }
  }
};

const attachListenerToPage = (
  pageId: string,
  dimension: number,
  listenersRef: MutableRefObject<Map<string, PageListener>>,
  generatePreviewForPage: (pageId: string, dimension: number) => Promise<void>,
  poster: Poster
): void => {
  const existing = listenersRef.current.get(pageId);
  if (existing && existing.dimension >= dimension) {
    return;
  }

  const page = poster.pages.pagesHashMap[pageId];
  if (!page) {
    return;
  }

  if (existing) {
    detachListener(existing);
  }

  listenersRef.current.set(pageId, {
    dimension,
    ...createDebouncedModifiedListener(page, pageId, dimension, generatePreviewForPage),
  });
};

export function useAttachPagePreviewRefreshListener(config: PagePreviewConfig): void {
  const dispatch = useAppDispatch();

  const pageIds = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages.pageOrder;
  });
  const currentPageId = useAppSelector((state) => {
    return state.posterEditor.currentPageId;
  });

  const {allPagesBatchedPreviews, currentPageLivePreview, allPagesLivePreview} = config;

  const lastGeneratedStates = useRef(new Map<string, PageGenerationState>());
  const inFlightRef = useRef(new Map<string, InFlightGenerationState>());
  const batchAbortRef = useRef<AbortController | null>(null);
  const currentPageListenerRef = useRef<CurrentPageListener | null>(null);
  const allPagesListenersRef = useRef(new Map<string, PageListener>());

  const generatePreviewForPage = useCallback(
    async (pageId: string, dimension: number, abortSignal?: AbortSignal): Promise<void> => {
      const inFlight = inFlightRef.current.get(pageId);
      if (inFlight) {
        queueHigherDimensionPending(inFlight, dimension, abortSignal ?? null);
        return;
      }

      const poster = window.posterEditor?.whiteboard;
      const page = poster?.pages.pagesHashMap[pageId];
      if (!poster || !page) {
        return;
      }

      const state: InFlightGenerationState = {dimension, pending: null};
      inFlightRef.current.set(pageId, state);

      try {
        const dataUrl = await generatePreviewDataUrl(page, dimension);
        if (isPreviewResultCommittable(dataUrl, state, poster, pageId, abortSignal)) {
          lastGeneratedStates.current.set(pageId, {lastModified: page.lastModified, dimension});
          dispatch(updatePagePreview({pageHashedId: pageId, previewUrl: dataUrl}));
        }
      } finally {
        const pending = state.pending;
        inFlightRef.current.delete(pageId);
        if (pending !== null) {
          void generatePreviewForPage(pageId, pending.dimension, pending.abortSignal ?? undefined);
        }
      }
    },
    [dispatch]
  );

  const refreshCurrentPagePreviewIfStale = useCallback((): (() => void) | undefined => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster || !currentPageLivePreview.enabled || !currentPageId) {
      return undefined;
    }

    const page = poster.pages.pagesHashMap[currentPageId];
    if (!page) {
      return undefined;
    }

    if (!isPageStale(lastGeneratedStates.current, currentPageId, page, currentPageLivePreview.requiredDimension)) {
      return undefined;
    }

    const idleCallbackId = scheduleIdleCallback(() => {
      void generatePreviewForPage(currentPageId, currentPageLivePreview.requiredDimension);
    });

    return (): void => {
      cancelCallback(idleCallbackId);
    };
  }, [currentPageLivePreview.enabled, currentPageLivePreview.requiredDimension, currentPageId, generatePreviewForPage]);

  const refreshAllStalePagePreviews = useCallback((): (() => void) | undefined => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster || !pageIds) {
      return undefined;
    }

    removePreviewsForDeletedPages(pageIds, lastGeneratedStates.current, dispatch);

    if (!allPagesBatchedPreviews.enabled || allPagesBatchedPreviews.requiredDimension === 0) {
      return undefined;
    }

    const pagesToGenerate = collectStalePageIds(pageIds, poster, allPagesBatchedPreviews.requiredDimension, lastGeneratedStates.current);
    if (pagesToGenerate.length === 0) {
      return undefined;
    }

    batchAbortRef.current?.abort();
    const abortController = new AbortController();
    batchAbortRef.current = abortController;

    generatePreviewsInBatches(pagesToGenerate, abortController, generatePreviewForPage, allPagesBatchedPreviews.requiredDimension);

    return (): void => {
      abortController.abort();
    };
  }, [pageIds, allPagesBatchedPreviews.enabled, allPagesBatchedPreviews.requiredDimension, generatePreviewForPage, dispatch]);

  const attachPreviewRefreshListenerToCurrentPage = useCallback((): (() => void) | undefined => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster || !currentPageLivePreview.enabled || !currentPageId) {
      detachCurrentPageListener(currentPageListenerRef);
      return undefined;
    }

    const page = poster.pages.pagesHashMap[currentPageId];
    if (!page) {
      return undefined;
    }

    if (isExistingListenerSufficient(currentPageListenerRef.current, currentPageId, currentPageLivePreview.requiredDimension)) {
      return undefined;
    }

    detachCurrentPageListener(currentPageListenerRef);

    const dimension = currentPageLivePreview.requiredDimension;

    currentPageListenerRef.current = {
      pageId: currentPageId,
      dimension,
      ...createDebouncedModifiedListener(page, currentPageId, dimension, generatePreviewForPage),
    };

    return (): void => {
      detachCurrentPageListener(currentPageListenerRef);
    };
  }, [currentPageLivePreview.enabled, currentPageLivePreview.requiredDimension, currentPageId, generatePreviewForPage]);

  const attachPreviewRefreshListenersToAllPages = useCallback((): (() => void) | undefined => {
    const poster = window.posterEditor?.whiteboard;

    if (!poster || !pageIds || !allPagesLivePreview.enabled) {
      detachAllPagesListeners(allPagesListenersRef);
      return undefined;
    }

    const dimension = allPagesLivePreview.requiredDimension;

    pruneListenersForRemovedPages(pageIds, allPagesListenersRef);

    for (const pageId of pageIds) {
      attachListenerToPage(pageId, dimension, allPagesListenersRef, generatePreviewForPage, poster);
    }

    return (): void => {
      detachAllPagesListeners(allPagesListenersRef);
    };
  }, [pageIds, allPagesLivePreview.enabled, allPagesLivePreview.requiredDimension, generatePreviewForPage]);

  useEffect(refreshCurrentPagePreviewIfStale, [refreshCurrentPagePreviewIfStale]);
  useEffect(refreshAllStalePagePreviews, [refreshAllStalePagePreviews]);
  useEffect(attachPreviewRefreshListenerToCurrentPage, [attachPreviewRefreshListenerToCurrentPage]);
  useEffect(attachPreviewRefreshListenersToAllPages, [attachPreviewRefreshListenersToAllPages]);
}
